import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Star, Trash2, GripVertical, LayoutTemplate } from "lucide-react";

const DOC_TYPES = [
  ["quote", "Quotation"], ["sales_order", "Sales Order"], ["sales_invoice", "Sales Invoice"],
  ["purchase_requisition", "Purchase Requisition"], ["purchase_order", "Purchase Order"],
  ["purchase_bill", "Purchase Bill"], ["journal", "Journal Voucher"],
];

const BLOCK_TYPES = [
  ["text", "Text / Heading"], ["party", "Party block"], ["meta", "Meta (doc # / date)"],
  ["lines", "Lines table"], ["totals", "Totals"], ["terms", "Terms & Conditions"],
  ["signature", "Signature"], ["spacer", "Spacer"],
];

function BlockEditor({ block, onChange, onDelete, onMoveUp, onMoveDown }) {
  return (
    <div className="border rounded-sm p-3 space-y-2 bg-card">
      <div className="flex items-center gap-2">
        <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
        <Select value={block.type} onValueChange={(v) => onChange({ ...block, type: v })}>
          <SelectTrigger className="rounded-sm h-8 w-40" data-testid={`block-type-${block.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{BLOCK_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex-1" />
        <button className="text-xs text-muted-foreground hover:text-accent" onClick={onMoveUp}>↑</button>
        <button className="text-xs text-muted-foreground hover:text-accent" onClick={onMoveDown}>↓</button>
        <button className="text-muted-foreground hover:text-destructive" onClick={onDelete} data-testid={`block-del-${block.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      {(block.type === "text" || block.type === "terms") && (
        <div>
          <Label className="text-[10px] font-mono uppercase text-muted-foreground">Content · supports {"{{org.name}}"}, {"{{amount_in_words}}"}, {"{{branch.gstin}}"}, {"{{customer.gstin}}"}</Label>
          <Input value={block.content || ""} onChange={(e) => onChange({ ...block, content: e.target.value })} className="rounded-sm mt-1 font-mono text-xs" data-testid={`block-content-${block.id}`} />
        </div>
      )}
      {block.type === "party" && (
        <Input value={block.label || "BILL TO"} onChange={(e) => onChange({ ...block, label: e.target.value })} className="rounded-sm h-8 text-xs" placeholder="Label (BILL TO / SHIP TO / SUPPLIER)" />
      )}
      {(block.type === "text" || block.type === "terms") && (
        <div className="flex gap-2 items-center text-xs">
          <label className="flex items-center gap-1"><input type="checkbox" checked={block.style?.bold || false} onChange={(e) => onChange({ ...block, style: { ...(block.style || {}), bold: e.target.checked } })} /> Bold</label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={block.style?.accent || false} onChange={(e) => onChange({ ...block, style: { ...(block.style || {}), accent: e.target.checked } })} /> Accent</label>
          <Select value={block.style?.size || "base"} onValueChange={(v) => onChange({ ...block, style: { ...(block.style || {}), size: v } })}>
            <SelectTrigger className="rounded-sm h-7 w-24 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{["sm","base","lg","xl"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

export default function Templates() {
  const [docType, setDocType] = useState("sales_invoice");
  const [tpls, setTpls] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const r = await api.get("/templates", { params: { doc_type: docType } });
    setTpls(r.data);
    const def = r.data.find((t) => t.is_default) || r.data[0];
    setSelected(def || null);
  };
  useEffect(() => { load(); }, [docType]);

  const setBlock = (i, next) => setSelected({ ...selected, blocks: selected.blocks.map((b, j) => j === i ? next : b) });
  const addBlock = () => setSelected({ ...selected, blocks: [...selected.blocks, { id: Math.random().toString(36).slice(2), type: "text", content: "New text", style: {} }] });
  const removeBlock = (i) => setSelected({ ...selected, blocks: selected.blocks.filter((_, j) => j !== i) });
  const moveBlock = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= selected.blocks.length) return;
    const arr = [...selected.blocks]; [arr[i], arr[j]] = [arr[j], arr[i]];
    setSelected({ ...selected, blocks: arr });
  };

  const save = async () => {
    try { await api.patch(`/templates/${selected.id}`, { name: selected.name, blocks: selected.blocks, config: selected.config });
      toast.success("Saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setDefault = async () => {
    try { await api.post(`/templates/${selected.id}/default`); toast.success("Set as default"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const dup = async () => {
    try { const r = await api.post("/templates", { doc_type: selected.doc_type, name: `Copy of ${selected.name}`, blocks: selected.blocks, config: selected.config });
      toast.success("Duplicated"); await load(); setSelected(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${selected.name}"?`)) return;
    try { await api.delete(`/templates/${selected.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const createNew = async () => {
    try { const r = await api.post("/templates", { doc_type: docType, name: "New template" });
      toast.success("Created"); await load(); setSelected(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="templates-page">
      <PageHeader eyebrow="Documents" title="Document Templates"
        description="Customize every printable document — quotes, orders, invoices, bills, and journals. Blocks are rearranged, edited, and styled to match your brand." />

      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-[10px] font-mono uppercase text-muted-foreground">Doc type</Label>
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger className="rounded-sm h-9 w-56" data-testid="tpl-doctype"><SelectValue /></SelectTrigger>
          <SelectContent>{DOC_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={createNew} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-template-btn"><Plus className="w-3.5 h-3.5" /> New template</Button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="card-sharp p-3 space-y-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground px-2 py-1">Templates</div>
          {tpls.length === 0 && <div className="text-xs text-muted-foreground p-4 text-center">No templates</div>}
          {tpls.map((t) => (
            <button key={t.id} onClick={() => setSelected(t)} data-testid={`tpl-select-${t.id}`}
              className={`w-full text-left p-2 rounded-sm text-sm hover:bg-muted flex items-center gap-2 ${selected?.id === t.id ? "bg-accent/10 border border-accent/30" : ""}`}>
              <LayoutTemplate className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="flex-1 truncate">{t.name}</span>
              {t.is_default && <Star className="w-3.5 h-3.5 text-accent fill-current" />}
            </button>
          ))}
        </div>

        <div className="md:col-span-2 card-sharp p-4 space-y-3">
          {selected ? (
            <>
              <div className="flex items-center gap-2 pb-3 border-b flex-wrap">
                <Input value={selected.name} onChange={(e) => setSelected({ ...selected, name: e.target.value })} className="rounded-sm h-9 flex-1 min-w-40" data-testid="tpl-name" />
                <Button variant="outline" size="sm" onClick={dup} className="rounded-sm text-xs" data-testid="tpl-duplicate">Duplicate</Button>
                {!selected.is_default && <Button variant="outline" size="sm" onClick={setDefault} className="rounded-sm text-xs gap-1" data-testid="tpl-set-default"><Star className="w-3 h-3" /> Default</Button>}
                {!selected.is_default && <Button variant="ghost" size="sm" onClick={remove} className="rounded-sm text-xs text-destructive" data-testid="tpl-del">Delete</Button>}
                <Button onClick={save} className="rounded-sm bg-accent hover:bg-accent/90 text-white text-xs" data-testid="tpl-save">Save</Button>
              </div>

              <div className="grid grid-cols-3 gap-3 pb-3 border-b">
                <div><Label className="text-[10px] font-mono uppercase text-muted-foreground">Accent color</Label><Input type="color" value={selected.config?.accent_color || "#EA580C"} onChange={(e) => setSelected({ ...selected, config: { ...selected.config, accent_color: e.target.value } })} className="rounded-sm mt-1 h-9" /></div>
                <div className="flex items-end gap-2"><Switch checked={!!selected.config?.show_qr} onCheckedChange={(v) => setSelected({ ...selected, config: { ...selected.config, show_qr: v } })} /><Label className="text-xs mb-1">Show QR (e-invoice)</Label></div>
                <div className="flex items-end gap-2"><Switch checked={!!selected.config?.show_bank_details} onCheckedChange={(v) => setSelected({ ...selected, config: { ...selected.config, show_bank_details: v } })} /><Label className="text-xs mb-1">Show bank details</Label></div>
              </div>

              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Blocks — use ↑↓ to reorder</div>
              <div className="space-y-2">
                {selected.blocks.map((b, i) => (
                  <BlockEditor key={b.id} block={b}
                    onChange={(next) => setBlock(i, next)}
                    onDelete={() => removeBlock(i)}
                    onMoveUp={() => moveBlock(i, -1)}
                    onMoveDown={() => moveBlock(i, +1)} />
                ))}
                <Button variant="outline" size="sm" onClick={addBlock} className="w-full rounded-sm gap-1.5" data-testid="add-block-btn"><Plus className="w-3.5 h-3.5" /> Add block</Button>
              </div>
            </>
          ) : (
            <div className="text-center text-muted-foreground py-10">Pick a template to edit</div>
          )}
        </div>
      </div>
    </div>
  );
}
